import React, { useEffect, useMemo, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Tag, Percent, Users, Zap } from "lucide-react";

// Unified codes admin panel (Fase 8).
// Shows referral + discount + hybrid in one table with type filters.
export default function StaffCodigos() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("superadmin_dcc");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (kind) params.kind = kind;
    if (status) params.status = status;
    api.get("/admin/codes/unified", { params })
      .then((r) => setRows(r.data.items || []))
      .catch(() => toast.error("Error al cargar códigos"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [kind, status]);

  const counts = useMemo(() => {
    const c = { referral: 0, discount: 0, hybrid: 0 };
    for (const r of rows) c[r.code_type] = (c[r.code_type] || 0) + 1;
    return c;
  }, [rows]);

  return (
    <StaffLayout user={user} superadmin={isSuper}>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Fase 8 · Códigos</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Códigos unificados</h1>
          <p className="mt-1 text-sm text-gray-400">
            Referrals de aliados + descuentos de catálogo + híbridos (aplican descuento Y generan comisión).
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="codigos-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm min-h-[44px]"
        >
          <Plus className="w-4 h-4" /> Nuevo código
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="codigos-stats">
        <StatCard label="Total" value={rows.length} icon={Tag} />
        <StatCard label="Referral" value={counts.referral || 0} icon={Users} />
        <StatCard label="Discount" value={counts.discount || 0} icon={Percent} />
        <StatCard label="Hybrid" value={counts.hybrid || 0} icon={Zap} accent />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <FilterPill value={kind} onSelect={setKind} options={[
          ["", "Todos"], ["referral", "Referral"], ["discount", "Discount"], ["hybrid", "Hybrid"],
        ]} label="Tipo" testidPrefix="filter-kind" />
        <FilterPill value={status} onSelect={setStatus} options={[
          ["", "Todos"], ["active", "Activo"], ["revoked", "Revocado"],
        ]} label="Status" testidPrefix="filter-status" />
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm" data-testid="codigos-table">
          <thead className="bg-white/5 text-left text-[10px] uppercase tracking-widest text-gray-500">
            <tr>
              <th className="p-3">Código</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Owner</th>
              <th className="p-3">Valor</th>
              <th className="p-3">Programas</th>
              <th className="p-3">Usos</th>
              <th className="p-3">Expira</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>
          <tbody className="text-gray-300">
            {loading && <tr><td colSpan={8} className="p-6 text-center text-gray-500">Cargando…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-gray-500">Sin códigos con estos filtros.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`code-row-${r.code}`} className="border-t border-white/5">
                <td className="p-3 font-mono text-white">{r.code}</td>
                <td className="p-3"><CodeBadge kind={r.code_type} /></td>
                <td className="p-3">{r.owner_name || <span className="text-gray-600">—</span>}</td>
                <td className="p-3 font-semibold text-white">{r.value}</td>
                <td className="p-3">{r.applies_to_program_ids?.length ? `${r.applies_to_program_ids.length} programas` : "Todos"}</td>
                <td className="p-3 text-xs">{r.used_count}{r.max_uses ? ` / ${r.max_uses}` : ""}</td>
                <td className="p-3 text-xs">{r.expires_at ? new Date(r.expires_at).toLocaleDateString("es-MX") : "—"}</td>
                <td className="p-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    r.status === "active" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
                  }`}>{r.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <NewCodeModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
    </StaffLayout>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`p-4 rounded-xl border ${accent ? "bg-[#F26A21]/10 border-[#F26A21]/40" : "bg-white/5 border-white/10"}`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</p>
        <Icon className={`w-4 h-4 ${accent ? "text-[#F26A21]" : "text-gray-400"}`} />
      </div>
      <p className="mt-2 text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

function CodeBadge({ kind }) {
  const map = {
    referral: { bg: "bg-brand-orange-500/15", fg: "text-brand-orange-300", label: "Referral" },
    discount: { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "Discount" },
    hybrid: { bg: "bg-[#F26A21]/20", fg: "text-[#F26A21]", label: "Hybrid" },
  };
  const m = map[kind] || map.referral;
  return <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${m.bg} ${m.fg}`}>{m.label}</span>;
}

function FilterPill({ value, onSelect, options, label, testidPrefix }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</span>
      {options.map(([val, lab]) => (
        <button
          key={val || "all"}
          onClick={() => onSelect(val)}
          data-testid={`${testidPrefix}-${val || "all"}`}
          className={`text-xs px-3 py-1 rounded-full transition-colors ${
            value === val ? "bg-[#F26A21] text-[#0B0B0B] font-bold" : "bg-white/5 text-gray-300 hover:bg-white/10"
          }`}
        >{lab}</button>
      ))}
    </div>
  );
}

function NewCodeModal({ onClose, onDone }) {
  const [type, setType] = useState("referral");
  const [code, setCode] = useState("");
  const [aliadoType, setAliadoType] = useState("reseller");
  const [aliadoId, setAliadoId] = useState("");
  const [aliados, setAliados] = useState({ resellers: [], influencers: [] });
  const [commissionPctOverride, setCommissionPctOverride] = useState("");
  const [discountPct, setDiscountPct] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/resellers"),
      api.get("/admin/influencers"),
    ]).then(([r, i]) => setAliados({ resellers: r.data.items, influencers: i.data.items }));
  }, []);

  const submit = async () => {
    if (!code) return toast.error("Código requerido");
    setBusy(true);
    try {
      if (type === "referral") {
        if (!aliadoId) throw new Error("Elige un aliado");
        const endpoint = aliadoType === "reseller"
          ? `/admin/resellers/${aliadoId}/referral-codes`
          : `/admin/influencers/${aliadoId}/referral-codes`;
        await api.post(endpoint, {
          code,
          commission_pct_override: commissionPctOverride ? parseFloat(commissionPctOverride) : undefined,
          max_uses: maxUses ? parseInt(maxUses) : undefined,
        });
      } else if (type === "hybrid") {
        if (!aliadoId) throw new Error("Elige un aliado");
        if (!discountPct) throw new Error("Falta % descuento");
        await api.post("/admin/codes/hybrid", {
          code,
          owner_type: aliadoType,
          owner_id: aliadoId,
          discount_pct: parseFloat(discountPct),
          commission_pct_override: commissionPctOverride ? parseFloat(commissionPctOverride) : undefined,
          max_uses: maxUses ? parseInt(maxUses) : undefined,
        });
      } else {
        // discount only
        if (!discountPct) throw new Error("Falta % descuento");
        await api.post("/admin/discount-codes", {
          code,
          type: "percent",
          value: parseFloat(discountPct),
          max_uses: maxUses ? parseInt(maxUses) : undefined,
        });
      }
      toast.success(`Código ${code.toUpperCase()} creado`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-[#111827] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-code-modal"
      >
        <h2 className="text-xl font-bold text-white font-display">Crear código</h2>
        <div className="mt-4 flex gap-2" data-testid="code-type-picker">
          {[["referral", "Referral"], ["discount", "Discount"], ["hybrid", "Hybrid"]].map(([v, l]) => (
            <button key={v} onClick={() => setType(v)}
              data-testid={`code-type-${v}`}
              className={`flex-1 py-2 rounded-md text-sm font-semibold min-h-[44px] ${
                type === v ? "bg-[#F26A21] text-[#0B0B0B]" : "bg-white/5 text-gray-300"
              }`}>{l}</button>
          ))}
        </div>
        {type === "hybrid" && (
          <p className="mt-2 text-xs text-[#F26A21]/80">
            Hybrid: cliente obtiene descuento Y el aliado gana comisión sobre el neto de venta.
          </p>
        )}

        <div className="mt-4 space-y-3">
          <ModalField label="Código *" val={code} on={(v) => setCode(v.toUpperCase())} tid="nc-code" />
          {(type === "referral" || type === "hybrid") && (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Tipo de aliado</label>
                <div className="mt-1 flex gap-2">
                  {["reseller", "influencer"].map((t) => (
                    <button key={t} onClick={() => { setAliadoType(t); setAliadoId(""); }}
                      className={`flex-1 py-2 rounded-md text-sm capitalize ${
                        aliadoType === t ? "bg-white/10 text-white" : "bg-white/5 text-gray-400"
                      }`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Aliado</label>
                <select value={aliadoId} onChange={(e) => setAliadoId(e.target.value)}
                  data-testid="nc-aliado-select"
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
                  <option value="">— Elige —</option>
                  {aliados[aliadoType === "reseller" ? "resellers" : "influencers"].map((a) =>
                    <option key={a.id} value={a.id}>{a.company_name || a.name} · {a.commission_pct}%</option>
                  )}
                </select>
              </div>
              <ModalField label="% comisión (override, opcional)" val={commissionPctOverride}
                on={setCommissionPctOverride} tid="nc-comm-override" />
            </>
          )}
          {(type === "discount" || type === "hybrid") && (
            <ModalField label="% descuento *" val={discountPct} on={setDiscountPct} tid="nc-discount" />
          )}
          <ModalField label="Máx. usos (opcional)" val={maxUses} on={setMaxUses} tid="nc-maxuses" />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-gray-300 text-sm min-h-[44px]">Cancelar</button>
          <button onClick={submit} disabled={busy} data-testid="nc-submit-btn"
            className="px-6 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
            {busy ? "Creando…" : "Crear"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalField({ label, val, on, tid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{label}</label>
      <input data-testid={tid} value={val} onChange={(e) => on(e.target.value)}
        className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F26A21]" />
    </div>
  );
}
